import { cn } from '@/lib/utils';
import { getSessionPatientDisplayName } from '../utils/getSessionPatientDisplayName';
import {
	buildCalendarDayDisplayItems,
	countCalendarSlotOverflowSessions,
	formatLocalDate,
	formatSessionTimeRange,
	getCalendarMonthDisplayItemHeight,
	isCancelledSession,
	sortSessionsForCalendarDisplay,
} from '../utils/calendarLogic';
import { useModal } from '@/hooks/useModal';
import { DaySessionsModal } from './DaySessionsModal';
import { ProfessionalSessionModal } from './ProfessionalSessionModal';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

const STATUS_COLORS = {
	CANCELLED: 'bg-red-50 text-red-700 border-red-100 hover:bg-red-100',
	FINISHED: 'bg-zinc-100 text-zinc-700 border-zinc-200 hover:bg-zinc-200',
	STARTED:
		'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
	CREATED: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
	CONFIRMED:
		'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
} as Record<Sessions.SessionStatus, string>;

interface CalendarMonthViewProps {
	days: { date: Date; isCurrentMonth: boolean }[];
	sessions: Sessions.Session[];
}

export const CalendarMonthView = ({
	days,
	sessions,
}: CalendarMonthViewProps) => {
	const weekDays = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];

	const [selectedSession, setSelectedSession] =
		useState<Sessions.Session | null>(null);
	const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);

	const sessionsByDay = sessions.reduce(
		(acc, session) => {
			const dateKey = formatLocalDate(new Date(session.selected_date));
			if (!acc[dateKey]) acc[dateKey] = [];
			acc[dateKey].push(session);
			return acc;
		},
		{} as Record<string, Sessions.Session[]>,
	);

	// Função centralizada para abrir o detalhe da sessão
	const handleOpenSessionDetail = (session: Sessions.Session) => {
		setSelectedSession(session);
		setIsSessionModalOpen(true);
	};

	const rowCount = days.length / 7;

	return (
		<div className="flex h-full w-full flex-col overflow-hidden bg-white">
			<div className="grid shrink-0 grid-cols-7 border-b border-zinc-100 bg-white">
				{weekDays.map((d) => (
					<div
						key={d}
						className="py-2 text-center text-[11px] font-black tracking-widest text-zinc-400 md:py-3"
					>
						{d}
					</div>
				))}
			</div>

			<div
				className="grid min-h-0 flex-1 grid-cols-7 border-l border-zinc-100"
				style={{ gridTemplateRows: `repeat(${rowCount}, 1fr)` }}
			>
				{days.map((dayObj, index) => (
					<MonthDayCell
						key={index}
						dayObj={dayObj}
						sessions={
							sessionsByDay[formatLocalDate(dayObj.date)] || []
						}
						onOpenSession={handleOpenSessionDetail}
					/>
				))}
			</div>

			<ProfessionalSessionModal
				session={selectedSession}
				isOpen={isSessionModalOpen}
				onClose={() => setIsSessionModalOpen(false)}
			/>
		</div>
	);
};

const MonthDayCell = ({
	dayObj,
	sessions,
	onOpenSession,
}: {
	dayObj: { date: Date; isCurrentMonth: boolean };
	sessions: Sessions.Session[];
	onOpenSession: (session: Sessions.Session) => void;
}) => {
	const { openModal, closeModal } = useModal();
	const containerRef = useRef<HTMLDivElement>(null);
	const [capacity, setCapacity] = useState(0);

	const isToday = dayObj.date.toDateString() === new Date().toDateString();
	const SESSION_ROW_GAP = 2;
	const displayItems = useMemo(
		() => buildCalendarDayDisplayItems(sessions),
		[sessions],
	);

	useLayoutEffect(() => {
		const measure = () => {
			if (!containerRef.current) return;

			const availableHeight = containerRef.current.offsetHeight;
			let usedHeight = 0;
			let visibleCount = 0;

			for (const _item of displayItems) {
				const rowHeight = getCalendarMonthDisplayItemHeight();
				const nextHeight =
					visibleCount === 0
						? rowHeight
						: usedHeight + SESSION_ROW_GAP + rowHeight;

				if (nextHeight > availableHeight) break;

				usedHeight = nextHeight;
				visibleCount += 1;
			}

			setCapacity(visibleCount);
		};
		measure();
		window.addEventListener('resize', measure);
		return () => window.removeEventListener('resize', measure);
	}, [displayItems]);

	const visibleItems = displayItems.slice(0, capacity);
	const heightHiddenCount = Math.max(0, displayItems.length - capacity);
	const slotOverflowCount = countCalendarSlotOverflowSessions(sessions);
	const remainingCount = heightHiddenCount + slotOverflowCount;

	return (
		<div
			className={cn(
				'flex min-h-0 flex-col overflow-hidden border-b border-r border-zinc-100 p-1',
				!dayObj.isCurrentMonth && 'bg-zinc-50/40',
			)}
		>
			<span
				className={cn(
					'mb-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-bold',
					dayObj.isCurrentMonth ? 'text-zinc-800' : 'text-zinc-300',
					isToday && 'bg-brand-purple text-white',
				)}
			>
				{dayObj.date.getDate()}
			</span>

			<div
				ref={containerRef}
				className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-hidden"
			>
				{visibleItems.map((item) => {
					const session = item.session;
					const sessionTimeRange = formatSessionTimeRange(
						session.selected_date,
					);

					return (
						<div
							key={session.id}
							onClick={() => onOpenSession(session)}
							className={cn(
								'flex h-[20px] shrink-0 cursor-pointer items-center truncate rounded-md border px-1.5 text-[9px] font-bold transition-all md:text-[10px]',
								isCancelledSession(session)
									? 'border-red-200 bg-red-50 text-red-700'
									: STATUS_COLORS[session.status] ||
											'border-blue-100 bg-blue-50 text-blue-700',
							)}
						>
							<span className="block min-w-0 truncate">
								{isCancelledSession(session) ? (
									<>
										<span className="font-bold">
											Cancelado
										</span>{' '}
										<span className="font-semibold">
											{sessionTimeRange}
										</span>
									</>
								) : (
									<>
										<span className="font-semibold">
											{sessionTimeRange}
										</span>{' '}
										<span className="font-bold">
											{getSessionPatientDisplayName(
												session,
											)}
										</span>
									</>
								)}
							</span>
						</div>
					);
				})}

				{remainingCount > 0 && (
					<button
						onClick={(e) => {
							e.stopPropagation();
							openModal({
								content: (
									<DaySessionsModal
										date={dayObj.date}
										sessions={sortSessionsForCalendarDisplay(
											sessions,
										)}
										onSelectSession={(
											session: Sessions.Session,
										) => {
											// CORRIGIDO: Nome da prop e lógica
											closeModal();
											onOpenSession(session);
										}}
									/>
								),
								title: (
									<span className="text-lg font-bold text-zinc-800">
										Sessões do dia
									</span>
								),
								size: 'compact',
							});
						}}
						className="h-[20px] shrink-0 pl-1.5 text-left text-[9px] font-black text-zinc-400 transition-colors hover:text-zinc-600 focus:outline-none md:text-[10px]"
					>
						+ {remainingCount} mais
					</button>
				)}
			</div>
		</div>
	);
};
